import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import { formatCurrency, formatRate, PerilType } from './utility';

interface ComparePerilsProps {
  quotePeril: PerilType | null;
  comparisonPeril: PerilType | null;
  hideRate?: boolean;
}

const NoMatchingPeril = () => {
  return (
    <Typography variant='body2' color='text.secondary'>
      There is no matching peril
    </Typography>
  );
};

export const ComparePerils = ({
  quotePeril,
  comparisonPeril,
  hideRate = false,
}: ComparePerilsProps) => {
  const hasQuotePeril = quotePeril !== null;
  const hasComparisonPeril = comparisonPeril !== null;
  const rowCount = hideRate ? 3 : 4; // Limit, Deductible, Premium, Rate (optional)

  const fields = [
    {
      label: 'Limit',
      comparisonValue: comparisonPeril?.limit ?? null,
      quoteValue: quotePeril?.limit ?? null,
      format: formatCurrency,
    },
    {
      label: 'Deductible',
      comparisonValue: comparisonPeril?.deductible ?? null,
      quoteValue: quotePeril?.deductible ?? null,
      format: formatCurrency,
    },
    {
      label: 'Premium',
      comparisonValue: comparisonPeril?.premium ?? null,
      quoteValue: quotePeril?.premium ?? null,
      format: formatCurrency,
    },
    ...(hideRate
      ? []
      : [
          {
            label: 'Rate',
            comparisonValue: comparisonPeril?.rate ?? null,
            quoteValue: quotePeril?.rate ?? null,
            format: formatRate,
          },
        ]),
  ];

  return (
    <PerilsComparisonContainer>
      <PerilsTable>
        <TableBody>
          {fields.map((field, index) => {
            const comparisonValue = field.comparisonValue;
            const quoteValue = field.quoteValue;
            const bothExist = !!comparisonValue && !!quoteValue;
            const comparisonLarger = bothExist && comparisonValue > quoteValue;
            const quoteLarger = bothExist && quoteValue > comparisonValue;
            const areEqual = bothExist && comparisonValue === quoteValue;

            const ComparisonCell =
              !hasComparisonPeril && index === 0
                ? PerilValueCell
                : comparisonLarger
                ? PerilValueCellGreen
                : areEqual || !bothExist
                ? PerilValueCell
                : PerilValueCellRed;

            const QuoteCell =
              !hasQuotePeril && index === 0
                ? PerilValueCell
                : quoteLarger
                ? PerilValueCellGreen
                : areEqual || !bothExist
                ? PerilValueCell
                : PerilValueCellRed;

            return (
              <TableRow key={field.label}>
                <PerilLabelCell>
                  <Typography variant='body2'>{field.label}</Typography>
                </PerilLabelCell>
                {!hasComparisonPeril && index === 0 ? (
                  <ComparisonCell rowSpan={rowCount}>
                    <NoMatchingPeril />
                  </ComparisonCell>
                ) : hasComparisonPeril ? (
                  <ComparisonCell>
                    <Typography variant='body2'>
                      {comparisonValue !== null
                        ? field.format(comparisonValue)
                        : '-'}
                    </Typography>
                  </ComparisonCell>
                ) : null}
                {!hasQuotePeril && index === 0 ? (
                  <QuoteCell rowSpan={rowCount}>
                    <NoMatchingPeril />
                  </QuoteCell>
                ) : hasQuotePeril ? (
                  <QuoteCell>
                    <Typography variant='body2'>
                      {quoteValue !== null ? field.format(quoteValue) : '-'}
                    </Typography>
                  </QuoteCell>
                ) : null}
              </TableRow>
            );
          })}
        </TableBody>
      </PerilsTable>
    </PerilsComparisonContainer>
  );
};

const PerilsComparisonContainer = styled(Box)({});

const PerilsTable = styled(Table)({
  width: '100%',
});

const PerilLabelCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  borderRight: `1px solid ${theme.palette.divider}`,
  width: '33.33%',
}));

const PerilValueCell = styled(TableCell)({
  width: '33.33%',
});

const PerilValueCellGreen = styled(TableCell)(({ theme }) => ({
  width: '33.33%',
  borderBottom: `2px solid ${theme.palette.success.main}`,
}));

const PerilValueCellRed = styled(TableCell)(({ theme }) => ({
  width: '33.33%',
  borderBottom: `2px solid ${theme.palette.error.main}`,
}));
